// Library Methods
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import ExamCardContainer from "../card-ui/ExamCardContainer/ExamCardContainer";
import ExamCardHeader from "../card-ui/ExamCardHeader/ExamCardHeader";
import ExamCardButton from "../card-ui/ExamCardButton/ExamCardButton";
import ExamCardToggles from "../card-ui/ExamCardToggles/ExamCardToggles";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

// Constants
import { DEFAULT_FUSION_AND_VERGENCE_CARD } from "src/constants/enums";

type FusionAndVergenceCardType = {
  label: string;
  selectedExamCard: SelectedExamCardType | undefined;
  updateExamCardProperty: (
    propertyName: keyof SelectedExamCardType,
    value: SelectedExamCardType[keyof SelectedExamCardType]
  ) => void;
  onRemoveCard: () => void;
};

const FusionAndVergenceCard = ({
  label,
  selectedExamCard,
  updateExamCardProperty,
  onRemoveCard,
}: FusionAndVergenceCardType) => {
  const { t } = useTranslation();

  const examCardInfo = selectedExamCard ?? DEFAULT_FUSION_AND_VERGENCE_CARD;

  const { fusionalAmplitudes, vergenceFunction, optionalToggles } =
    examCardInfo;

  const booleanOptions = useMemo(
    () => [
      { value: true, label: t("On") },
      { value: false, label: t("Off") },
    ],
    [t]
  );

  const textStyle = {
    color: "#FFF",
    fontSize: "12px",
  };

  // Truncates to a single line with an ellipsis instead of wrapping onto a
  // second line -- minWidth: 0 is needed alongside the usual overflow trio
  // since Typography sits as a flex item here (its default min-width: auto
  // would otherwise keep it from shrinking below the text's full width).
  const titleStyle = {
    ...textStyle,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  };

  // A button only needs to wrap to a new line once its own label can't fit
  // -- not at some arbitrary shared width. "ch" scales with character
  // count, so each button's floor tracks its own text; +2ch covers the
  // button's horizontal padding/border.
  const buttonMinWidth = (buttonLabel: string) => `${buttonLabel.length + 2}ch`;

  return (
    <ExamCardContainer>
      <ExamCardHeader isNew label={label} onRemoveCard={onRemoveCard} />
      <Grid
        item
        container
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        gap="8px"
        flexGrow={1}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          {/* Fusional Amplitudes */}
          <Grid
            item
            display="flex"
            flexDirection="column"
            gap="4px"
            flex="0 0 calc(50% - 4px)"
            minWidth={0}
          >
            <TitleWithTooltip
              title={t("Fusional Amplitudes")}
              tooltip={t(
                "Checks if the patient fuses the two half-pictures into one, then drives them apart and back to find the convergence, divergence, vertical and cyclofusion ranges."
              )}
              titleStyle={titleStyle}
              tooltipStyle={textStyle}
            />

            <Grid container item display="flex" flexWrap="wrap" gap="4px">
              {booleanOptions.map((option) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(option.label)}
                  key={String(option.value)}
                >
                  <ExamCardButton
                    isSelected={
                      option.value
                        ? Boolean(fusionalAmplitudes)
                        : !fusionalAmplitudes
                    }
                    updateValue={() => {
                      updateExamCardProperty(
                        "fusionalAmplitudes",
                        option.value
                      );

                      // at least one module must be true
                      if (!vergenceFunction && !option.value) {
                        updateExamCardProperty("vergenceFunction", true);
                      }
                    }}
                    label={option.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Vergence Function */}
          <Grid
            item
            display="flex"
            flexDirection="column"
            gap="4px"
            flex="0 0 calc(50% - 4px)"
            minWidth={0}
          >
            <TitleWithTooltip
              title={t("Vergence Function")}
              tooltip={t(
                "Measures near point of convergence and vergence facility — how close a target can approach before it doubles, and how quickly the eyes re-fuse when it jumps near and far."
              )}
              titleStyle={titleStyle}
              tooltipStyle={textStyle}
            />

            <Grid container item display="flex" flexWrap="wrap" gap="4px">
              {booleanOptions.map((option) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(option.label)}
                  key={String(option.value)}
                >
                  <ExamCardButton
                    isSelected={
                      option.value
                        ? Boolean(vergenceFunction)
                        : !vergenceFunction
                    }
                    updateValue={() => {
                      updateExamCardProperty("vergenceFunction", option.value);

                      // at least one module must be true
                      if (!option.value && !fusionalAmplitudes) {
                        updateExamCardProperty("fusionalAmplitudes", true);
                      }
                    }}
                    label={option.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Divider color="#5B6D7A" />

          <ExamCardToggles
            optionalToggles={optionalToggles}
            updateExamCardProperty={updateExamCardProperty}
            showToggles={DEFAULT_FUSION_AND_VERGENCE_CARD.optionalToggles}
          />
        </Box>
      </Grid>
    </ExamCardContainer>
  );
};

export default FusionAndVergenceCard;
